"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useCallback, useLayoutEffect, useRef } from "react";

import {
  invalidateDashboardQueries,
  shouldRefreshDashboardOnHistoryRestore,
} from "@/lib/dashboard/invalidate-dashboard-queries";
import { isNotebookPathname } from "@/lib/notebook/core/notebook-route-entry";
import { useRefetchOnHistoryRestore } from "@/hooks/useRefetchOnHistoryRestore";

/**
 * Keep dashboard React Query data fresh when leaving /notebook/* (browser Back,
 * bfcache, or in-app router.push to /home, /library, etc.).
 */
export function DashboardHistoryRestore() {
  const qc = useQueryClient();
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    const prev = pathnameRef.current;
    pathnameRef.current = pathname;
    if (isNotebookPathname(prev) && !isNotebookPathname(pathname)) {
      invalidateDashboardQueries(qc);
    }
  }, [pathname, qc]);

  const refresh = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!shouldRefreshDashboardOnHistoryRestore(window.location.pathname)) {
      return;
    }
    invalidateDashboardQueries(qc);
  }, [qc]);

  useRefetchOnHistoryRestore(refresh);
  return null;
}
